/** Envoltura estándar para respuestas de la API: `{ success, data }`. */
export interface ApiResponse<T> {
  data: T;
  message?: string;
  success: boolean;
}

/** Paginación estándar del backend. */
export interface Pagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

/** Respuesta de listado ya normalizada para consumo en componentes. */
export interface ListResult<T> {
  items: T[];
  pagination?: Pagination;
  /** Contadores por estado/pestaña cuando el endpoint los manda (`data.counts`). */
  counts?: Record<string, number>;
}

/** Sentido de ordenamiento de una columna. */
export type SortDir = 'asc' | 'desc';

/**
 * Columna y sentido por los que está ordenada una tabla.
 *
 * `by` es la CLAVE de la columna en la API (`createdAt`, `email`…), no el
 * nombre de la columna en SQL: cada endpoint traduce esa clave contra su
 * propia lista blanca, así que el cliente nunca decide qué se interpola.
 */
export interface SortState {
  by: string;
  dir: SortDir;
}

/** Parámetros comunes de consulta para listados. */
export interface QueryParams {
  page?: number;
  pageSize?: number;
  search?: string;
  from?: string;
  to?: string;
  sortBy?: string;
  sortDir?: SortDir;
  [key: string]: unknown;
}

/** Vuelca un `SortState` en los parámetros que espera el backend. */
export function sortParams(sort: SortState | null | undefined): QueryParams {
  return sort ? { sortBy: sort.by, sortDir: sort.dir } : {};
}

/**
 * Ordena una lista YA CARGADA en memoria.
 *
 * Solo para tablas que tienen todos sus datos (las que filtran en cliente).
 * Una tabla paginada en servidor NO debe usar esto: ordenaría únicamente la
 * página visible, que es justo lo que hace parecer que el orden no sirve.
 *
 * `accessors` traduce la clave de columna al valor por el que se compara.
 */
export function sortItems<T>(
  items: readonly T[],
  sort: SortState | null | undefined,
  accessors: Record<string, (item: T) => unknown>,
): T[] {
  const get = sort ? accessors[sort.by] : undefined;
  if (!sort || !get) return [...items];

  const factor = sort.dir === 'asc' ? 1 : -1;
  return [...items].sort((a, b) => {
    const va = get(a);
    const vb = get(b);
    const emptyA = va == null || va === '';
    const emptyB = vb == null || vb === '';
    // Los vacíos quedan al final en AMBOS sentidos: no se invierten con el
    // factor, o al ordenar descendente la tabla abriría con una fila de guiones.
    if (emptyA || emptyB) return emptyA && emptyB ? 0 : emptyA ? 1 : -1;
    return compareValues(va, vb) * factor;
  });
}

/** Números como números, booleanos por valor y texto con locale español. */
function compareValues(a: unknown, b: unknown): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);

  return String(a).localeCompare(String(b), 'es', { numeric: true, sensitivity: 'base' });
}

/**
 * Calcula el estado siguiente al pulsar una columna: si ya se ordena por ella
 * se invierte el sentido; si no, se empieza por `defaultDir`.
 */
export function toggleSort(
  current: SortState | null | undefined,
  by: string,
  defaultDir: SortDir = 'asc',
): SortState {
  if (current?.by === by) return { by, dir: current.dir === 'asc' ? 'desc' : 'asc' };
  return { by, dir: defaultDir };
}

/**
 * Normaliza las distintas formas de respuesta de listado del backend
 * (`{ success, data: [...] , pagination }`, `{ data: { items } }`, arreglo plano…)
 * a un `ListResult<T>` uniforme.
 */
export function unwrapList<T>(res: unknown): ListResult<T> {
  const r = res as Record<string, any> | T[] | null | undefined;

  if (Array.isArray(r)) return { items: r };
  if (!r || typeof r !== 'object') return { items: [] };

  const body = r as Record<string, any>;
  let pagination: Pagination | undefined = body['pagination'] ?? body['data']?.['pagination'];

  // Paginación estilo legacy (page/limit/total planos, sin objeto `pagination`).
  if (!pagination && body['total'] != null && body['page'] != null) {
    const pageSize = Number(body['pageSize'] ?? body['limit']) || 0;
    const total = Number(body['total']) || 0;
    pagination = {
      page: Number(body['page']) || 1,
      pageSize,
      total,
      totalPages: pageSize ? Math.ceil(total / pageSize) : 0,
    };
  }

  const data = body['data'];
  const counts: Record<string, number> | undefined =
    data && typeof data === 'object' && !Array.isArray(data) && data['counts'] && typeof data['counts'] === 'object'
      ? (data['counts'] as Record<string, number>)
      : undefined;
  if (counts) {
    const result = unwrapList<T>({ ...body, data: { ...data, counts: undefined } });
    return { ...result, counts };
  }

  if (Array.isArray(data)) return { items: data, pagination };
  if (data && typeof data === 'object') {
    for (const key of ['items', 'rows', 'results', 'list']) {
      if (Array.isArray(data[key])) return { items: data[key], pagination };
    }
    // data es un objeto con una sola colección adentro (p. ej. { payments: [...] })
    const arrays = Object.values(data).filter(Array.isArray) as T[][];
    if (arrays.length === 1) return { items: arrays[0], pagination };
  }
  for (const key of ['items', 'rows', 'results', 'list']) {
    if (Array.isArray(body[key])) return { items: body[key], pagination };
  }
  // Colección con nombre a nivel raíz (p. ej. { offers: [...] }, { requestedParts: [...] }).
  const topArrays = Object.values(body).filter(Array.isArray) as T[][];
  if (topArrays.length === 1) return { items: topArrays[0], pagination };

  return { items: [], pagination };
}

/** Extrae el objeto `data` de una respuesta `{ success, data }` (o devuelve el cuerpo). */
export function unwrapItem<T>(res: unknown): T {
  const body = res as Record<string, any> | null | undefined;
  if (body && typeof body === 'object' && 'data' in body && body['data'] !== undefined) {
    return body['data'] as T;
  }
  return res as T;
}
